/**
 * NewCardModal Component - Create new EA-Agile cards
 * Modal form for creating cards via the API
 */

import React, { useState } from 'react';
import { FiX, FiSave } from 'react-icons/fi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface NewCardModalProps {
  onClose: () => void;
  onSuccess: () => void;
  defaultState?: string;
  defaultCardType?: string;
  clientId: string;
}

// Card type options matching backend enum
const CARD_TYPES = [
  { value: 'EPIC', label: 'Epic' },
  { value: 'STORY', label: 'Story' },
  { value: 'TASK', label: 'Task' },
  { value: 'STANDARD', label: 'Standard' }
];

// Status options matching backend enum
const STATUS_OPTIONS = [
  { value: 'BACKLOG', label: 'Backlog' },
  { value: 'TODO', label: 'To Do' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'REVIEW', label: 'Review' },
  { value: 'DONE', label: 'Done' }
];

// Priority options (1-5)
const PRIORITY_OPTIONS = [
  { value: 1, label: '1 - Critical' },
  { value: 2, label: '2 - High' },
  { value: 3, label: '3 - Medium' },
  { value: 4, label: '4 - Low' },
  { value: 5, label: '5 - Lowest' }
];

// Board options
const BOARD_OPTIONS = [
  { value: '1', label: 'Development' },
  { value: '2', label: 'Standards' },
  { value: '3', label: 'Docs' }
];

const NewCardModal: React.FC<NewCardModalProps> = ({ onClose, onSuccess, defaultState = 'BACKLOG', defaultCardType = 'TASK', clientId }) => {
  const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email') || 'unknown';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    card_type: defaultCardType,
    status: defaultState,
    priority: 3,
    board_id: '1',
    assigned_to: '',
    sprint_number: '',
    story_points: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!clientId) {
      setError('Authentication required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const requestBody = {
        Client_ID: clientId,
        board_id: formData.board_id,
        card_type: formData.card_type,
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        status: formData.status,
        priority: Number(formData.priority),
        assigned_to: formData.assigned_to.trim() || undefined,
        sprint_number: formData.sprint_number ? Number(formData.sprint_number) : undefined,
        story_points: formData.story_points ? Number(formData.story_points) : undefined,
        created_by: userEmail,
        tags: []
      };

      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.CARDS.CREATE,
        'POST',
        requestBody
      );

      if (isSuccessResponse(response)) {
        // Success - close modal and refresh
        onSuccess();
        onClose();
      } else {
        throw new Error(response.message || 'Failed to create card');
      }
    } catch (err) {
      console.error('Error creating card:', err);
      setError(err instanceof Error ? err.message : 'Failed to create card');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Create New Card</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {/* Title */}
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter card title"
              required
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter card description"
            />
          </div>

          {/* Row: Card Type, Status, Priority */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="card_type" className="block text-sm font-medium text-gray-700 mb-1">
                Card Type <span className="text-red-500">*</span>
              </label>
              <select
                id="card_type"
                name="card_type"
                value={formData.card_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {CARD_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {STATUS_OPTIONS.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {PRIORITY_OPTIONS.map(priority => (
                  <option key={priority.value} value={priority.value}>{priority.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row: Board, Sprint, Story Points */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="board_id" className="block text-sm font-medium text-gray-700 mb-1">
                Board
              </label>
              <select
                id="board_id"
                name="board_id"
                value={formData.board_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {BOARD_OPTIONS.map(board => (
                  <option key={board.value} value={board.value}>{board.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="sprint_number" className="block text-sm font-medium text-gray-700 mb-1">
                Sprint
              </label>
              <input
                type="number"
                id="sprint_number"
                name="sprint_number"
                value={formData.sprint_number}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Sprint #"
              />
            </div>

            <div>
              <label htmlFor="story_points" className="block text-sm font-medium text-gray-700 mb-1">
                Story Points
              </label>
              <input
                type="number"
                id="story_points"
                name="story_points"
                value={formData.story_points}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Points"
              />
            </div>
          </div>

          {/* Assigned To */}
          <div className="mb-4">
            <label htmlFor="assigned_to" className="block text-sm font-medium text-gray-700 mb-1">
              Assigned To
            </label>
            <input
              type="email"
              id="assigned_to"
              name="assigned_to"
              value={formData.assigned_to}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="assignee@example.com"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              isSubmitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <FiSave className="w-4 h-4" />
            {isSubmitting ? 'Creating...' : 'Create Card'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewCardModal;
